
import React, { useState, useEffect } from 'react';

const API_URL = '';

export default function StatsPanel({ token, setActiveTab }) {
  const [stats, setStats] = useState({
    totalProducts: 0,
    totalPreorders: 0,
    totalVisitors: 0,
    visitsToday: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      // FIX: Added /api/ prefix
      const response = await fetch(`${API_URL}/api/admin/stats`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      setStats({
        totalProducts: data.totalProducts || 0,
        totalPreorders: data.totalPreorders || 0,
        totalVisitors: data.visitors?.total || 0,
        visitsToday: data.visitors?.today || 0
      });
      setLoading(false);
    } catch (error) {
      console.error('Error fetching stats:', error);
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={styles.loading}>
        <div style={styles.spinner}></div>
        <p style={{ color: '#fff', marginTop: '20px' }}>Loading dashboard...</p>
      </div>
    );
  }

  const statCards = [
    {
      title: 'Total Products',
      value: stats.totalProducts,
      icon: '📦',
      color: '#ce0021',
      trend: '+12%'
    },
    {
      title: 'Total Preorders',
      value: stats.totalPreorders,
      icon: '🎯',
      color: '#ff6b35',
      trend: '+8%'
    },
    {
      title: 'Total Visitors',
      value: stats.totalVisitors,
      icon: '👥',
      color: '#4ecdc4',
      trend: '+15%'
    },
    {
      title: 'Visits Today',
      value: stats.visitsToday,
      icon: '📈',
      color: '#95e1d3',
      trend: '+3%'
    }
  ];

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div>
          <h2 style={styles.title}>Dashboard Overview</h2>
          <p style={styles.subtitle}>Welcome back! Here's what's happening with your store.</p>
        </div>
        <button style={styles.refreshButton} onClick={fetchStats}>
          🔄 Refresh
        </button>
      </div>

      <div style={styles.statsGrid}>
        {statCards.map((card, index) => (
          <div key={index} style={styles.statCard}>
            <div style={styles.statHeader}>
              <div style={{
                ...styles.iconCircle,
                background: `${card.color}20`
              }}>
                <span style={{ fontSize: '28px' }}>{card.icon}</span>
              </div>
              <span style={styles.trend}>{card.trend}</span>
            </div>
            <h3 style={{
              ...styles.statValue,
              color: card.color
            }}>
              {card.value.toLocaleString()}
            </h3>
            <p style={styles.statLabel}>{card.title}</p>
          </div>
        ))}
      </div>

      <div style={styles.quickActions}>
        <h3 style={styles.sectionTitle}>Quick Actions</h3>
        <div style={styles.actionsGrid}>
          <button style={styles.actionButton} onClick={() => setActiveTab('upload')}>
            <div style={styles.actionIcon}>➕</div>
            <div style={styles.actionText}>
              <span style={styles.actionTitle}>Add Product</span>
              <span style={styles.actionDesc}>Upload new items</span>
            </div>
          </button>

          <button style={styles.actionButton} onClick={() => setActiveTab('preorders')}>
            <div style={styles.actionIcon}>📋</div>
            <div style={styles.actionText}>
              <span style={styles.actionTitle}>View Preorders</span>
              <span style={styles.actionDesc}>Manage orders</span>
            </div>
          </button>

          <button style={styles.actionButton} onClick={() => setActiveTab('featured')}>
            <div style={styles.actionIcon}>⭐</div>
            <div style={styles.actionText}>
              <span style={styles.actionTitle}>Featured Items</span>
              <span style={styles.actionDesc}>Set homepage products</span>
            </div>
          </button>

          <button style={styles.actionButton} onClick={() => setActiveTab('visitor')}>
            <div style={styles.actionIcon}>📊</div>
            <div style={styles.actionText}>
              <span style={styles.actionTitle}>Analytics</span>
              <span style={styles.actionDesc}>View detailed stats</span>
            </div>
          </button>
        </div>
      </div>
    </div>
  );
}

const styles = {
  container: {
    padding: '30px',
    backgroundColor: '#0a0a0a',
    minHeight: '100vh',
    color: '#fff'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '40px',
    flexWrap: 'wrap',
    gap: '16px'
  },
  title: {
    fontSize: '32px',
    fontWeight: 'bold',
    color: '#ce0021',
    margin: '0 0 8px 0'
  },
  subtitle: {
    fontSize: '16px',
    color: '#999',
    margin: 0
  },
  refreshButton: {
    padding: '10px 20px',
    background: 'rgba(206, 0, 33, 0.1)',
    border: '1px solid rgba(206, 0, 33, 0.3)',
    borderRadius: '8px',
    color: '#ce0021',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '600',
    transition: 'all 0.3s'
  },
  loading: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '400px',
    backgroundColor: '#0a0a0a'
  },
  spinner: {
    width: '50px',
    height: '50px',
    border: '4px solid rgba(206, 0, 33, 0.1)',
    borderTop: '4px solid #ce0021',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite'
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '24px',
    marginBottom: '40px'
  },
  statCard: {
    background: 'linear-gradient(135deg, #1a1a1a 0%, #0f0f0f 100%)',
    border: '1px solid rgba(206, 0, 33, 0.2)',
    borderRadius: '16px',
    padding: '24px',
    transition: 'all 0.3s',
    cursor: 'pointer'
  },
  statHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px'
  },
  iconCircle: {
    width: '60px',
    height: '60px',
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  trend: {
    fontSize: '14px',
    color: '#4ecdc4',
    fontWeight: '600'
  },
  statValue: {
    fontSize: '36px',
    fontWeight: 'bold',
    margin: '8px 0'
  },
  statLabel: {
    fontSize: '14px',
    color: '#999',
    margin: 0
  },
  quickActions: {
    marginTop: '40px'
  },
  sectionTitle: {
    fontSize: '20px',
    fontWeight: 'bold',
    color: '#fff',
    marginBottom: '20px',
    borderLeft: '4px solid #ce0021',
    paddingLeft: '12px'
  },
  actionsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: '20px'
  },
  actionButton: {
    background: 'linear-gradient(135deg, #1a1a1a 0%, #0f0f0f 100%)',
    border: '1px solid rgba(206, 0, 33, 0.2)',
    borderRadius: '12px',
    padding: '20px',
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    cursor: 'pointer',
    transition: 'all 0.3s',
    color: '#fff',
    width: '100%'
  },
  actionIcon: {
    fontSize: '32px',
    width: '56px',
    height: '56px',
    background: 'rgba(206, 0, 33, 0.1)',
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0
  },
  actionText: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '4px'
  },
  actionTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#fff'
  },
  actionDesc: {
    fontSize: '13px',
    color: '#999'
  }
};

// Add keyframes for spinner animation
const styleSheet = document.createElement("style");
styleSheet.textContent = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;
document.head.appendChild(styleSheet);